import { ApiResponse } from 'types';
import type { IVRNode, IVRNodeType, ContextType, ActionType } from '../../types/ivr';
import type { StateDetail } from './stateDetails.service';
import { apiError } from '../apiError';
import axiosInstance from '../axios.config';

/**
 * Deploy a visual IVR flow (node graph) into `state_details` rows using the
 * EXISTING, authenticated /telephony/ivr/state-details CRUD API.
 *
 * The old builder pointed at /ivr/state-details/deploy, which has no handler on
 * this backend (and used an unauthenticated fetch). This translates the graph
 * client-side and upserts each state through stateDetailsService (which carries
 * the JWT and, on the backend, pushes each row to the voiceflow runtime).
 *
 * Translation is keyed on the runtime contract:
 *   - The AGI starts every call at the state named MAIN_MENU, so the entry node
 *     is deployed under that name; other nodes get a sanitized name derived from
 *     their label/id. nextState references are resolved via that id->name map.
 *   - action_details / type_meta are written as JSON strings (the live schema
 *     stores them as TEXT), with lowercase context/action enums.
 *
 * Edges: the node model encodes transitions via menu `options[].nextNode` and an
 * api_call's onSuccess/onError. Nodes whose next-state isn't encoded that way are
 * deployed as terminal states and reported in `warnings` — honest about the
 * builder model's limits rather than inventing edges.
 *
 * Deploy is a replace, scoped to (operator, service, language): states present in
 * the graph are created/updated; states of the SAME language no longer in the
 * graph are deleted. Other languages / outbound flows are left untouched.
 */

export interface DeployFlowInput {
  name: string;
  operatorId: string;
  serviceId: string;
  businessId?: string;
  businessName?: string;
  language?: string;
  nodes: IVRNode[];
}

export interface DeployFlowResult {
  statesCreated: number;
  statesUpdated: number;
  statesDeleted: number;
  flowName: string;
  operatorId: string;
  serviceId: string;
  language: string;
  message: string;
  deployedAt: string;
  stateNames: string[];
  warnings: string[];
  entryState: string;
}

const NODE_MAP: Record<IVRNodeType, { ctx: ContextType; action: ActionType }> = {
  greeting:     { ctx: 'bypass',     action: 'static' },
  menu:         { ctx: 'dtmf',       action: 'static' },
  submenu:      { ctx: 'dtmf',       action: 'static' },
  input:        { ctx: 'dtmf',       action: 'static' },
  announcement: { ctx: 'bypass',     action: 'static' },
  transfer:     { ctx: 'transfer',   action: 'static' },
  voicemail:    { ctx: 'voice_mail', action: 'static' },
  hangup:       { ctx: 'endcall',    action: 'static' },
  ai_agent:     { ctx: 'stream',     action: 'static' },
  api_call:     { ctx: 'dtmf',       action: 'api_call' },
  queue:        { ctx: 'dtmf',       action: 'static' },  // overridden to queue_<ext> below
  // ⚠ twiml has NO runtime. There is no TwiML concept in the AGI or the
  // dialplan, and node.twiml was never read here — the body was discarded and
  // the node deployed as a plain bypass playback. Kept only so saved flows
  // still load; it is withdrawn from the palette, and deploying one warns.
  twiml:        { ctx: 'bypass',     action: 'static' },
};

const sanitize = (s: string) =>
  (s || '').toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50);

/** entry node -> MAIN_MENU; everything else gets a unique sanitized name. */
function deriveNames(nodes: IVRNode[]): { idToName: Record<string, string>; entryState: string } {
  const entry = nodes.find(n => n.type === 'greeting') || nodes.find(n => n.type === 'menu') || nodes[0];
  const idToName: Record<string, string> = {};
  const used = new Set<string>();
  if (entry) { idToName[entry.id] = 'MAIN_MENU'; used.add('MAIN_MENU'); }
  for (const n of nodes) {
    if (idToName[n.id]) continue;
    const base = sanitize(n.label) || sanitize(n.id) || 'STATE';
    let name = base, i = 1;
    while (used.has(name)) { name = `${base.slice(0, 46)}_${++i}`; }
    used.add(name);
    idToName[n.id] = name;
  }
  return { idToName, entryState: entry ? 'MAIN_MENU' : '' };
}

function buildActionDetails(n: IVRNode): Record<string, any> {
  const ad: Record<string, any> = {
    text: n.audioConfig?.text ?? n.message ?? '',
    files: n.audioConfig?.audioFiles ?? [],
  };
  if (n.audioConfig?.useAudioFirst != null) ad.use_audio_first = n.audioConfig.useAudioFirst;
  if (n.type === 'api_call' && n.apiCall) {
    ad.api_name = n.label;
    ad.api_url = n.apiCall.url;
    ad.api_method = n.apiCall.method;
    // Response -> variables: the runtime extracts these JSON paths from the API
    // response and exposes them as {{user.<variable>}} for later states.
    if (Array.isArray(n.apiCall.responseMapping) && n.apiCall.responseMapping.length) {
      ad.api_response_mapping = n.apiCall.responseMapping
        .filter(m => m && m.variable && m.key)
        .map(m => ({ variable: m.variable, path: m.key }));
    }
  }
  if (n.type === 'queue' && n.queue) ad.queue_id = n.queue;
  if (n.type === 'transfer') {
    // Generic transfer: number (PSTN) | sip (dial/refer) | agent | other.
    const tt = n.transferType || (n.transferNumber ? 'number' : n.agentUrl ? 'agent' : undefined);
    if (tt === 'number' && n.transferNumber) ad.target_number = n.transferNumber;
    if (tt === 'sip') {
      // Flat + self-contained: snapshot the resolved endpoint + method. A
      // transfer profile is only a design-time convenience that fills these in;
      // the deployed state does NOT reference the profile (no runtime coupling),
      // so editing/deleting a profile never changes an already-deployed flow.
      if (n.sipEndpoint) ad.target_number = n.sipEndpoint;
      if (n.transferMethod) ad.transfer_method = n.transferMethod;
    }
    if (tt === 'other' && n.transferNumber) ad.target_number = n.transferNumber;
    // 'agent' transfers carry the wss URL in type_meta.input.value (see buildTypeMeta).
    if (n.transferMethod && !ad.transfer_method) ad.transfer_method = n.transferMethod;
  }
  if (n.type === 'ai_agent' && n.agentMode === 'internal' && n.aiAgent) ad.ai_agent = n.aiAgent;
  return ad;
}

function buildTypeMeta(n: IVRNode, idToName: Record<string, string>, warnings: string[]): Record<string, any> {
  const tm: Record<string, any> = { choices: [] as any[] };

  // Menu options -> choices[].nextState (DTMF branches).
  for (const o of n.options || []) {
    const next = o.nextNode ? idToName[o.nextNode] : undefined;
    if (!next) { warnings.push(`Option "${o.digit}" on "${n.label || n.id}" has no valid target node`); continue; }
    tm.choices.push({ key: o.digit, value: o.value || o.digit, msg: o.label || '', nextState: next });
  }

  // Single next-state transition -> type_meta.input.nextState (the runtime
  // convention for non-menu hops, e.g. ASK_QUESTION -> PROCESS_QUESTION).
  // Explicit node.nextNode wins; api_call falls back to its onSuccess target.
  const nextId = n.nextNode || (n.type === 'api_call' ? n.apiCall?.onSuccess : undefined);
  const input: Record<string, any> = {};
  if (nextId) {
    const target = idToName[nextId];
    if (target) input.nextState = target;
    else warnings.push(`"${n.label || n.id}" points to a next node that isn't in the flow`);
  }
  // Voice-agent wiring -> input.value (inbound) / input.valueOut (outbound). An
  // external (third-party) agent is just a wss:// URL the customer provides;
  // internal agents resolve the URL at runtime, so only emit when one is set.
  // Decoupled from node type: a menu/stream node that also points at an agent
  // round-trips its URL. (transfer-to-agent deploys as a stream node, see buildStates.)
  if (n.agentUrl) input.value = n.agentUrl;
  if (n.agentUrlOut) input.valueOut = n.agentUrlOut;
  if (Object.keys(input).length) tm.input = input;

  // api_call error branch as an explicit DTMF-less choice.
  if (n.type === 'api_call' && n.apiCall?.onError) {
    const err = idToName[n.apiCall.onError];
    if (err) tm.choices.push({ key: 'error', value: 'error', msg: 'API error', nextState: err });
  }

  const terminal: IVRNodeType[] = ['transfer', 'voicemail', 'hangup'];
  if (!tm.choices.length && !tm.input && !terminal.includes(n.type)) {
    warnings.push(`State "${idToName[n.id]}" (${n.type}) has no outgoing transition — deployed as terminal`);
  }
  return tm;
}

/** Translate a flow's nodes into state_details payloads (does not call the API). */
export function buildStates(input: DeployFlowInput): { states: StateDetail[]; entryState: string; warnings: string[] } {
  const language = input.language || 'en';
  const warnings: string[] = [];
  const nodes = input.nodes || [];
  if (!nodes.length) return { states: [], entryState: '', warnings: ['Flow has no nodes'] };

  const { idToName, entryState } = deriveNames(nodes);
  const states: StateDetail[] = nodes.map(n => {
    const m = NODE_MAP[n.type] || { ctx: 'dtmf' as ContextType, action: 'static' as ActionType };
    // "transfer to agent" routes through a voice stream, not a SIP transfer.
    let ctx: string = (n.type === 'transfer' && n.transferType === 'agent') ? 'stream' : m.ctx;

    // A ring group names its target IN the context, which is how the dialplan
    // dispatches it: `queue_2000` is matched by prefix and sent into [acd_queue]
    // with 2000 as the extension. Digits only — FILTER(0-9) on the switch strips
    // anything else, so a stray character would silently address another group.
    if (n.type === 'queue') {
      const ext = (n.queue || '').replace(/\D/g, '');
      if (!ext) {
        warnings.push(`"${n.label}" is a ring group but names no extension — the call would have nowhere to go`);
      } else {
        ctx = `queue_${ext}`;
      }
    }

    if (n.type === 'twiml') {
      warnings.push(`"${n.label}" is a TwiML node: there is no TwiML runtime, so it deploys as a plain announcement and its TwiML is not used`);
    }

    // What the switch already had wins, when the node kind still matches it.
    //
    // The editor's kinds are lossier than the runtime's: stream+api_call,
    // stream+function_call, dtmf+function_call and alert all load as a node whose
    // NODE_MAP entry says `static`, so re-saving an untouched flow rewrote 10 of
    // the 166 live states and turned the one alert into a bypass playback.
    //
    // Only re-emitted while the user has NOT changed the kind — checked by asking
    // the same reverse mapper the loader used. Change a node to a hangup and it
    // deploys as a hangup, as it should.
    let action: ActionType = m.action;
    if (n.runtimeContextType) {
      const loadedAs = stateToNodeType(n.runtimeContextType, n.runtimeAction, !!n.options?.length);
      if (loadedAs === n.type) {
        ctx = n.runtimeContextType;
        if (n.runtimeAction) action = n.runtimeAction as ActionType;
      }
    }
    return {
      operator_id: input.operatorId,
      service_id: input.serviceId,
      name: idToName[n.id],
      language,
      type: 'dtmf',
      action,
      context_type: ctx as ContextType,
      action_details: JSON.stringify(buildActionDetails(n)),
      type_meta: JSON.stringify(buildTypeMeta(n, idToName, warnings)),
      is_blocked: 0,
      business_name: input.businessName || 'default_business',
      attributes: '',
      call_request: 'inbound',
    };
  });
  return { states, entryState, warnings };
}

const stateKey = (s: { name?: string }) => (s.name || '').toUpperCase();

/** Inverse of NODE_MAP: a state's (context_type, action) -> a builder node type. */
function stateToNodeType(ctx?: string, action?: string, hasChoices?: boolean): IVRNodeType {
  // Parameterised first: queue_2000 is a queue, not an unknown context.
  if (ctx && ctx.startsWith('queue_')) return 'queue';
  switch (ctx) {
    case 'transfer': return 'transfer';
    case 'voice_mail': return 'voicemail';
    case 'endcall': return 'hangup';
    case 'stream': return 'ai_agent';
    case 'dtmf': return action === 'api_call' ? 'api_call' : (hasChoices ? 'menu' : 'input');
    case 'bypass':
    default: return action === 'api_call' ? 'api_call' : 'greeting';
  }
}

/**
 * Reverse of buildStates: turn deployed state_details rows back into a builder
 * node graph so an existing app can be loaded for editing. nextState references
 * resolve by name (we use the state name as the node id), so options[].nextNode
 * and nextNode point at the right nodes. action_details/type_meta arrive as JSON
 * strings (the live schema) and are parsed here.
 */
export function buildNodesFromStates(states: StateDetail[]): { nodes: IVRNode[]; entry?: string } {
  const nodes: IVRNode[] = [];
  for (const s of states) {
    let ad: any = {}, tm: any = {};
    try { ad = s.action_details ? JSON.parse(s.action_details) : {}; } catch { /* tolerate */ }
    try { tm = s.type_meta ? JSON.parse(s.type_meta) : {}; } catch { /* tolerate */ }
    const choices = Array.isArray(tm.choices) ? tm.choices : [];
    const type = stateToNodeType(s.context_type, s.action, choices.length > 0);
    const node: IVRNode = { id: s.name, type, label: s.name, message: ad.text || '' };
    // Kept verbatim so a save cannot rewrite what the node kind cannot express.
    if (s.context_type) node.runtimeContextType = s.context_type;
    if (s.action) node.runtimeAction = s.action;
    if (Array.isArray(ad.files) && ad.files.length) node.audioConfig = { audioFiles: ad.files };
    // Options keep ALL fields: key->digit, msg->label, value (destination/product/
    // agent URL), nextState->nextNode. Set on any node that has choices, so a
    // stream/menu hybrid shows its options.
    if (choices.length) {
      node.options = choices.map((c: any) => ({
        digit: String(c.key ?? ''), label: c.msg || '', value: c.value ?? '', nextNode: c.nextState || '',
      }));
    }
    if (tm.input && tm.input.nextState) node.nextNode = tm.input.nextState;
    // Voice-agent URL (input.value/valueOut) on any node — external if a URL is
    // present; internal managed agent otherwise.
    if (tm.input?.value) { node.agentUrl = tm.input.value; node.agentMode = 'external'; }
    if (tm.input?.valueOut) node.agentUrlOut = tm.input.valueOut;
    if (type === 'ai_agent') {
      if (!node.agentUrl && ad.ai_agent) node.aiAgent = ad.ai_agent;
      if (!node.agentMode) node.agentMode = 'internal';
    }
    if (type === 'api_call') {
      node.apiCall = {
        url: ad.api_url || '',
        method: (ad.api_method || 'GET'),
        onSuccess: tm.input?.nextState,
        responseMapping: Array.isArray(ad.api_response_mapping)
          ? ad.api_response_mapping.map((m: any) => ({ key: m.path, variable: m.variable }))
          : undefined,
      } as any;
    }
    if (type === 'transfer') {
      const num = ad.target_number || ad.transfer_number;
      if (ad.transfer_method) node.transferMethod = ad.transfer_method;
      // Flat discriminator: a transfer_method (refer/dial) means a SIP transfer;
      // a plain number has none. No profile reference is read — the state is
      // self-contained.
      if (ad.transfer_method || (num && /^sip:/i.test(num))) { node.transferType = 'sip'; node.sipEndpoint = num; }
      else if (num) { node.transferType = 'number'; node.transferNumber = num; }
      else if (node.agentUrl) node.transferType = 'agent';
      else { node.transferType = 'other'; if (ad.transfer_endpoint) node.transferEndpoint = ad.transfer_endpoint; }
    }
    nodes.push(node);
  }
  const entry = nodes.find(n => n.id === 'MAIN_MENU')?.id || nodes[0]?.id;
  return { nodes, entry };
}

/**
 * Turn the BACKEND importer's node JSON into builder nodes.
 *
 * <p>Sibling of {@link buildNodesFromStates}, and needed because the two loaders
 * read different things: that one reads cpass's own `state_details` rows, this
 * one reads `ivr_flows.nodes` as `IvrImportService` wrote them after pulling a
 * flow off a switch.
 *
 * It looked like no translation was needed — the importer emits node-shaped
 * objects with an id and a type — but the vocabularies only overlap. The
 * importer says `name`, `audioFiles` and `choices[{key,label,target}]`; the
 * builder wants `label`, `audioConfig.audioFiles` and
 * `options[{digit,label,nextNode}]`. Assigning the nodes straight through
 * therefore produced an editor with every field blank: a graph that looked
 * loaded and said nothing.
 */
export function nodesFromImportedFlow(raw: any[]): { nodes: IVRNode[]; entry?: string } {
  const nodes: IVRNode[] = [];
  for (const n of raw || []) {
    const choices = Array.isArray(n?.choices) ? n.choices : [];
    // Decided from the state's own context/action rather than trusting the
    // importer's `type` string, so both loaders agree on what a row is. The
    // importer has kinds this builder has no node for ('announcement',
    // 'unsupported'); this maps those onto the nearest thing it can render
    // instead of leaving an unknown type that no panel matches.
    const type = stateToNodeType(n?.contextType, n?.action, choices.length > 0);
    const node: IVRNode = {
      id: String(n?.id ?? ''),
      type,
      label: String(n?.name ?? n?.id ?? ''),
      message: '',
    } as IVRNode;
    // Kept verbatim for the same reason buildNodesFromStates keeps them: a save
    // must not rewrite a context this builder cannot express.
    if (n?.contextType) (node as any).runtimeContextType = n.contextType;
    if (n?.action) (node as any).runtimeAction = n.action;
    const files = Array.isArray(n?.audioFiles) ? n.audioFiles.filter((f: any) => f) : [];
    if (files.length) node.audioConfig = { audioFiles: files };
    if (choices.length) {
      node.options = choices.map((c: any) => ({
        digit: String(c?.key ?? ''), label: c?.label || '', value: '', nextNode: c?.target || '',
      }));
    }
    if (n?.nextNode) node.nextNode = String(n.nextNode);
    // The voice agent. `agentUrl`/`agentUrlOut` are where these switches record
    // it; `aiAgent` is a managed agent this platform resolves at call time.
    if (n?.agentUrl) { node.agentUrl = String(n.agentUrl); node.agentMode = 'external'; }
    if (n?.agentUrlOut) node.agentUrlOut = String(n.agentUrlOut);
    if (n?.aiAgent && !node.agentUrl) node.aiAgent = String(n.aiAgent);
    if (type === 'ai_agent' && !node.agentMode) node.agentMode = 'internal';
    if (n?.transferNumber) {
      node.transferNumber = String(n.transferNumber);
      node.transferType = /^sip:/i.test(String(n.transferNumber)) ? 'sip' : 'number';
      if (node.transferType === 'sip') node.sipEndpoint = String(n.transferNumber);
    }
    if (n?.queue) (node as any).queue = String(n.queue);
    if (n?.mailbox) (node as any).mailbox = String(n.mailbox);
    if (n?.apiUrl || n?.apiName) {
      node.apiCall = {
        url: String(n.apiUrl || ''),
        method: (n.apiMethod || 'GET'),
        onSuccess: n?.nextNode || undefined,
      } as any;
    }
    nodes.push(node);
  }
  const entry = nodes.find(n => n.id === 'MAIN_MENU')?.id || nodes[0]?.id;
  return { nodes, entry };
}

/**
 * RETIRED (Phase 0, docs/design/2026-10-14_ivr_app_management.md §1.2 step 2).
 *
 * This used to upsert each state through POST/PUT/DELETE /ivr/state-details/{id} —
 * handlers that do not exist on the backend — so "Deploy" could never store
 * anything, and the claim that the CRUD "pushes each row to the voiceflow runtime"
 * was not true. It also rendered states with this file's own renderer, which
 * disagrees with the backend's. The only path to a switch is now the backend-rendered
 * push (previewSwitchPush / applySwitchPush below). Kept so old callers fail loudly
 * instead of silently.
 */
export async function deployFlow(_input: DeployFlowInput): Promise<ApiResponse<DeployFlowResult>> {
  return {
    success: false,
    error: 'Deploy is retired: it called endpoints that do not exist. Use "Push to switch" (preview first).',
  };
}

/* ────────────────────────────────────────────────────────────────────────────
 * Pushing to the SWITCH — the database the AGI actually reads.
 *
 * deployFlow() above is retired. These two calls are the only path to a switch;
 * the backend renders, plans and audits the write.
 *
 * Preview is a separate call and the default, because writing to a switch that
 * is carrying calls is not something anyone should get by pressing Save.
 * ──────────────────────────────────────────────────────────────────────────── */

/** One state's planned write, field by field (backend SwitchPushPlanner). */
export interface SwitchPushWrite {
  name: string;
  action: 'INSERT' | 'UPDATE' | 'UNCHANGED';
  diff: Record<string, { before: unknown; after: unknown }>;
  after?: Record<string, unknown>;
}

/**
 * The plan the backend returns for a switch push (IvrSwitchPushService, Phase 0 of
 * docs/design/2026-10-14_ivr_app_management.md). Preview by default; `blockers`
 * non-empty means Apply is refused.
 */
export interface SwitchPushPlan {
  provider?: string;
  providerId?: number;
  homeProviders?: Array<{ id: number; name: string }>;
  needsProvider?: boolean;
  operatorId: string;
  serviceId: string;
  language?: string;
  callRequest?: { column: 'PRESENT' | 'ABSENT' | 'UNKNOWN'; evidence?: string; sent?: string | null };
  businessName?: { onSwitch: string[]; before?: string | null; after?: string | null; change: boolean };
  statesOnSwitchBefore?: number;
  statesOnSwitchAllLanguages?: number;
  otherLanguagesUntouched?: string[];
  writes?: SwitchPushWrite[];
  inserts?: number;
  updates?: number;
  unchanged?: number;
  willWrite?: string[];
  /** States no longer in the flow. NOT deleted by a push (Phase 3: 10 minutes after publish). */
  pendingRemoval?: Array<{ name: string; language?: string; call_request?: string | null }>;
  /** Always empty now: a push deletes nothing. Kept for older screens. */
  willDelete?: string[];
  applied: boolean;
  blockers?: string[];
  warnings?: string[];
  note?: string;
  pushId?: number;
  result?: 'APPLIED' | 'PARTIAL' | 'FAILED';
  rowsWritten?: number;
  rowsDeleted?: number;
  failures?: string[];
  /** Safe publish: send this back with apply; 409 if the switch changed since. */
  previewHash?: string;
  mode?: string;
  applyEnabled?: boolean;
  replayed?: boolean;
}

/** What the caller chooses for a push. Everything optional: the safe default is "keep what the switch has". */
export interface SwitchPushOptions {
  /** Which switch. Omit: the backend picks the switch the flow's numbers live on. Never hard-coded. */
  providerId?: number;
  /** The recording folder. Omit: the switch's existing business_name is kept. */
  businessName?: string;
  /** Required with a businessName that differs from the switch's. */
  confirmBusinessNameChange?: boolean;
}

/**
 * The flow, in the shape IVRFlowDeployDTO expects.
 *
 * Nodes are sent rather than the states this file can already build, so the
 * SWITCH copy is rendered by the same backend renderer that validates it. Two
 * renderers producing the rows that route live calls is the drift this platform
 * has already been bitten by.
 *
 * No business id: the recording folder is the switch's business_name, kept by the
 * backend unless SwitchPushOptions.businessName names a new one. Sending the org
 * UUID here is what used to move a flow's recordings.
 */
function toDeployPayload(input: DeployFlowInput) {
  return {
    name: input.name,
    operatorId: input.operatorId,
    serviceId: input.serviceId,
    language: input.language || 'en',
    nodes: input.nodes,
  };
}

async function pushToSwitch(
  input: DeployFlowInput,
  options: SwitchPushOptions,
  organizationId: string,
  apply: boolean,
  previewHash?: string,
): Promise<ApiResponse<SwitchPushPlan>> {
  try {
    const params: Record<string, unknown> = { organizationId, apply };
    if (previewHash) params.previewHash = previewHash;
    if (options.providerId != null) params.providerId = options.providerId;
    if (options.businessName) params.businessName = options.businessName;
    if (options.confirmBusinessNameChange) params.confirmBusinessNameChange = true;
    const res = await axiosInstance.post(`/ivr/state-details/push`, toDeployPayload(input), { params });
    return { success: true, data: res.data?.data as SwitchPushPlan };
  } catch (e) {
    // apiError IS the failure shape ({success:false, error, status, denied}),
    // so it is returned rather than wrapped — wrapping put the object where a
    // string belongs and only the real build caught it.
    return apiError(e, apply ? 'Could not write to the switch' : 'Could not read the switch');
  }
}

/** What WOULD change on the switch. Writes nothing. */
export function previewSwitchPush(input: DeployFlowInput, options: SwitchPushOptions, organizationId: string) {
  return pushToSwitch(input, options, organizationId, false);
}

/** Write it. Only ever call this after a preview the user has actually seen, with the same options. */
export function applySwitchPush(input: DeployFlowInput, options: SwitchPushOptions, organizationId: string,
                                previewHash?: string) {
  return pushToSwitch(input, options, organizationId, true, previewHash);
}

/** Field values for display: JSON text is shown as-is, null as an em dash. */
export function fieldValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'string' ? v : JSON.stringify(v);
}

/** Blocks Apply until the preview is clean, and while the backend's publish mode is not 'apply'. */
export function canApply(plan: SwitchPushPlan | null | undefined): boolean {
  return !!plan && !plan.needsProvider && !(plan.blockers && plan.blockers.length) && plan.applyEnabled !== false;
}
